import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  CalendarDays, MapPin, Users, IndianRupee, Trophy, CheckCircle, Loader2,
  ArrowLeft, Clock, Send, AlertTriangle
} from 'lucide-react';
import { LOGO_URL, BrandText } from '@/lib/brand';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicEnroll() {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [form, setForm] = useState({ team_name: '', captain_name: '', phone: '', email: '' });

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/public/events/${eventId}`)
      .then(r => r.json())
      .then(data => {
        if (data.detail) setEvent(null);
        else setEvent(data);
      })
      .catch(() => setEvent(null))
      .finally(() => setLoading(false));
  }, [eventId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.captain_name || !form.phone) { toast.error('Captain name and phone are required'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/public/events/${eventId}/enroll`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (res.ok) {
        setSuccess(data);
      } else {
        toast.error(data.detail || 'Registration failed');
      }
    } catch (err) { toast.error('Network error. Please try again.'); }
    finally { setSubmitting(false); }
  };

  const eventTypeColors = {
    Tournament: 'bg-orange-100 text-orange-700 border-orange-300',
    Camp: 'bg-blue-100 text-blue-700 border-blue-300',
    Trial: 'bg-purple-100 text-purple-700 border-purple-300',
    Workshop: 'bg-teal-100 text-teal-700 border-teal-300',
  };

  if (loading) return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
    </div>
  );

  if (!event) return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <Card className="max-w-md w-full"><CardContent className="p-10 text-center">
        <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
        <h2 className="text-xl font-display font-bold mb-2">Event Not Found</h2>
        <p className="text-sm text-muted-foreground mb-4">This event may have ended or the link is invalid.</p>
        <Link to="/"><Button variant="outline"><ArrowLeft className="h-4 w-4 mr-2" /> Go to Home</Button></Link>
      </CardContent></Card>
    </div>
  );

  // Success State
  if (success) return (
    <div className="min-h-screen bg-background" data-testid="enroll-success">
      <nav className="border-b border-border/40 bg-card/90 backdrop-blur-xl">
        <div className="max-w-3xl mx-auto flex items-center justify-between px-4 h-14">
          <Link to="/" className="flex items-center gap-2"><img src={LOGO_URL} alt="Out-Play" className="h-8 w-8 rounded-lg object-cover" /><BrandText className="text-lg" /></Link>
        </div>
      </nav>
      <div className="max-w-md mx-auto px-4 py-16 text-center">
        <div className="h-20 w-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="h-10 w-10 text-emerald-600" />
        </div>
        <h1 className="text-2xl font-display font-bold text-emerald-700 mb-2">Registration Successful!</h1>
        <p className="text-muted-foreground mb-4">You're registered for <strong>{success.event_title}</strong></p>
        <Card className="text-left mb-6"><CardContent className="p-5 space-y-2">
          <div className="flex justify-between text-sm"><span className="text-muted-foreground">Registration ID</span><span className="font-mono text-xs">{success.registration_id?.slice(0, 8)}...</span></div>
          <div className="flex justify-between text-sm"><span className="text-muted-foreground">Team</span><span className="font-medium">{form.team_name || 'Individual'}</span></div>
          <div className="flex justify-between text-sm"><span className="text-muted-foreground">Captain</span><span className="font-medium">{form.captain_name}</span></div>
          {success.entry_fee > 0 && <div className="flex justify-between text-sm"><span className="text-muted-foreground">Entry Fee</span><span className="font-semibold text-emerald-600">₹{success.entry_fee}</span></div>}
          <div className="flex justify-between text-sm"><span className="text-muted-foreground">Academy</span><span>{success.academy_name}</span></div>
        </CardContent></Card>
        <p className="text-xs text-muted-foreground">Please save this page or take a screenshot as your entry confirmation.</p>
      </div>
    </div>
  );

  // Enrollment Form
  const isFull = event.spots_left !== null && event.spots_left <= 0;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(window.location.href)}`;

  return (
    <div className="min-h-screen bg-background" data-testid="enroll-page">
      {/* Navbar */}
      <nav className="border-b border-border/40 bg-card/90 backdrop-blur-xl">
        <div className="max-w-3xl mx-auto flex items-center justify-between px-4 h-14">
          <Link to="/" className="flex items-center gap-2"><img src={LOGO_URL} alt="Out-Play" className="h-8 w-8 rounded-lg object-cover" /><BrandText className="text-lg" /></Link>
          <Badge variant="outline" className="text-xs">Public Registration</Badge>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="grid md:grid-cols-5 gap-6">
          {/* Event Details (3/5) */}
          <div className="md:col-span-3 space-y-5">
            {/* Hero Card */}
            <Card className="overflow-hidden">
              <div className="bg-gradient-to-br from-emerald-600 via-teal-600 to-emerald-700 p-6 text-white">
                <Badge className={`mb-3 ${eventTypeColors[event.event_type] || 'bg-slate-100 text-slate-700'}`}>{event.event_type}</Badge>
                <h1 className="text-2xl font-display font-bold mb-2">{event.title}</h1>
                <p className="text-emerald-100 text-sm">{event.academy_name}{event.academy_city ? ` • ${event.academy_city}` : ''}</p>
              </div>
              <CardContent className="p-5 space-y-3">
                <div className="flex items-center gap-3 text-sm"><CalendarDays className="h-4 w-4 text-muted-foreground flex-shrink-0" /><span>{event.date}{event.end_date && event.end_date !== event.date ? ` — ${event.end_date}` : ''}</span></div>
                {event.venue && <div className="flex items-center gap-3 text-sm"><MapPin className="h-4 w-4 text-muted-foreground flex-shrink-0" /><span>{event.venue}</span></div>}
                <div className="flex items-center gap-3 text-sm"><Users className="h-4 w-4 text-muted-foreground flex-shrink-0" /><span>{event.participant_count} registered{event.max_participants ? ` / ${event.max_participants} max` : ''}{event.spots_left !== null ? ` • ${event.spots_left} spots left` : ''}</span></div>
                {event.entry_fee > 0 && <div className="flex items-center gap-3 text-sm"><IndianRupee className="h-4 w-4 text-muted-foreground flex-shrink-0" /><span className="font-semibold">₹{event.entry_fee} entry fee</span></div>}
                {event.description && <><Separator /><p className="text-sm text-muted-foreground leading-relaxed">{event.description}</p></>}
              </CardContent>
            </Card>

            {/* QR Code */}
            <Card>
              <CardContent className="p-5 flex items-center gap-4">
                <img src={qrUrl} alt="QR Code" className="h-20 w-20 rounded-lg border" />
                <div>
                  <p className="text-sm font-semibold">Share this event</p>
                  <p className="text-xs text-muted-foreground mt-1">Scan the QR code or share the link to invite more participants</p>
                  <Button variant="outline" size="sm" className="mt-2 h-7 text-xs" onClick={() => { navigator.clipboard.writeText(window.location.href); toast.success('Link copied!'); }}>Copy Link</Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Registration Form (2/5) */}
          <div className="md:col-span-2">
            <Card className="sticky top-20">
              <CardContent className="p-5">
                <h3 className="text-lg font-display font-bold mb-1">Register Now</h3>
                <p className="text-xs text-muted-foreground mb-4">Fill in your details to participate</p>

                {isFull ? (
                  <div className="text-center py-8">
                    <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto mb-3" />
                    <p className="font-semibold">Registration Closed</p>
                    <p className="text-xs text-muted-foreground mt-1">This event has reached maximum capacity</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Team Name</Label>
                      <Input value={form.team_name} onChange={(e) => setForm({...form, team_name: e.target.value})} placeholder="e.g., Thunder FC" data-testid="enroll-team" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Captain / Player Name *</Label>
                      <Input value={form.captain_name} onChange={(e) => setForm({...form, captain_name: e.target.value})} placeholder="Full name" required data-testid="enroll-captain" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Phone Number *</Label>
                      <Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} placeholder="+91 98765..." required data-testid="enroll-phone" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Email</Label>
                      <Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} placeholder="email@example.com" data-testid="enroll-email" />
                    </div>
                    {event.entry_fee > 0 && (
                      <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800">
                        <p className="text-sm font-semibold text-amber-700">Entry Fee: ₹{event.entry_fee}</p>
                        <p className="text-xs text-amber-600 mt-0.5">Payment to be collected at the venue</p>
                      </div>
                    )}
                    <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700 h-11" disabled={submitting} data-testid="enroll-submit">
                      {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                      {submitting ? 'Registering...' : 'Register for Event'}
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-border bg-card py-6 mt-12">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-2 mb-2"><img src={LOGO_URL} alt="Out-Play" className="h-5 w-5 rounded object-cover" /><BrandText className="text-sm" /></div>
          <p className="text-xs text-muted-foreground">Powered by Out-Play.in — India's #1 Sports Academy Platform</p>
        </div>
      </footer>
    </div>
  );
}
